/**
 * Plan 场景 Chat 首条消息 builders — reference 协议。
 *
 * 铁律（perplexica docs/reference-type-architecture.md §1）：
 * - 正文只表达「用户实际输入了什么」；空正文时方括号非空=操作回显，方括号也为空=静默入口。
 * - 业务数据（goalId / title / summary 等）一律走 referenceId，后端 resolver 拉取注入，
 *   正文禁止携带 —— 后端已按 id 注入草稿/诊断快照，前端拼数据是语义污染。
 */

/**
 * Plan — 无草稿，按 checkup 维度进 Goal 引导（操作回显）。
 * dimensionCode 走 referenceId（后端 plan_guide resolver 从 referenceId 解析 `\d{3}`）。
 */
export const buildPlanNoDraftGoalQuestion = (
  dimensionCode: string,
  label: string,
): string => {
  const code = dimensionCode.trim();
  // label = 卡片本地化标题；缺失时静默，绝不回落 dimensionCode 或硬编码英文。
  return `[${label.trim()}](referenceType=plan_guide&referenceId=${code})\n`;
};

/**
 * Onboarding V3 plan-preview 草稿 — plan_activation（操作回显）。
 *
 * 空正文不进入模型，Ed 按 skill 流开场。草稿数据（title / 财务画像 / planPreview）
 * 由后端 resolver 按 referenceId 拉取注入。
 * 协议行本身不可删（后端路由依赖）；空方括号不渲染引用条。
 */
export const buildPlanActivationQuestion = ({
  draftGoalId,
  draftTitle,
}: {
  draftGoalId: string;
  draftTitle?: string;
}): string => {
  const id = draftGoalId.trim();
  return `[${draftTitle?.trim() ?? ""}](referenceType=plan_activation&referenceId=${id})\n`;
};

/**
 * Plan — 续写 inactive 草稿 Goal（操作回显）。
 * 草稿快照（title / steps / diagnosisContent）由后端 resolver 按 referenceId 拉取。
 */
export const buildPlanResumeDraftGoalQuestion = ({
  draftGoalId,
  draftTitle,
}: {
  draftGoalId: string;
  draftTitle?: string;
  summary?: string;
}): string => {
  const id = draftGoalId.trim();
  return `[${draftTitle?.trim() ?? ""}](referenceType=plan_guide_resume&referenceId=${id})\n`;
};

/**
 * Goal creator — 有品类方向（方向走 referenceId=GoalCategory 枚举，
 * resolver 注入 ScenarioContext.direction；卡片文案作为操作回显，不进消息正文）。
 */
export const buildGoalCreatorWithCategoryQuestion = (
  category: string,
  label: string,
): string => {
  const direction = category.trim().toUpperCase();
  const chip = label.trim();
  return direction
    ? `[${chip}](referenceType=goal_creator&referenceId=${direction})\n`
    : `[${chip}](referenceType=goal_creator)\n`;
};

/** Goal creator — 无方向操作回显 */
export const buildGoalCreatorQuestion = (label: string): string =>
  `[${label.trim()}](referenceType=goal_creator)\n`;

/**
 * Goal 详情 — Chat with Ed 操作回显；Goal 数据由 referenceId 解析。
 */
export const buildGoalReviewQuestion = ({
  goalId,
  goalTitle,
}: {
  goalId: string;
  goalTitle: string;
}): string => {
  const id = goalId.trim();
  return `[${goalTitle.trim()}](referenceType=goal_review&referenceId=${id})\n`;
};
